import { useState, useCallback, useEffect } from "react";
import { useBluelagoon } from "@bluelagoon";

export default function useFavorites() {
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [favoriteWorkflows, setFavoriteWorkflows] = useState([]);

    const { call: fetchFavorites } = useBluelagoon({
        method: "getUserFavorites",
    });
    const { call: addFavorite } = useBluelagoon({
        method: "addUserFavorite",
    });
    const { call: removeFavorite } = useBluelagoon({
        method: "removeUserFavorite",
    });

    const loadFavorites = useCallback(async () => {
        const res = await fetchFavorites({});
        if (res?.favorites) {
            setFavoriteIds(new Set(res.favorites.map((f) => f.workflowId)));
            setFavoriteWorkflows(res.favorites.map((f) => f.workflow));
        }
    }, [fetchFavorites]);

    useEffect(() => {
        loadFavorites();
    }, [loadFavorites]);

    const toggleFavorite = useCallback(
        async (id) => {
            const wasFavorite = favoriteIds.has(id);
            // Optimistic update
            setFavoriteIds((prev) => {
                const next = new Set(prev);
                wasFavorite ? next.delete(id) : next.add(id);
                return next;
            });

            try {
                if (wasFavorite) {
                    await removeFavorite({ workflowId: id });
                } else {
                    await addFavorite({ workflowId: id });
                }
                // Reload full list to get workflow details
                await loadFavorites();
            } catch {
                // Revert on failure
                setFavoriteIds((prev) => {
                    const reverted = new Set(prev);
                    wasFavorite ? reverted.add(id) : reverted.delete(id);
                    return reverted;
                });
            }
        },
        [favoriteIds, addFavorite, removeFavorite, loadFavorites]
    );

    const isFavorite = useCallback(
        (id) => favoriteIds.has(id),
        [favoriteIds]
    );

    return {
        favorites: favoriteIds,
        favoriteWorkflows,
        toggleFavorite,
        isFavorite,
        reloadFavorites: loadFavorites,
    };
}
